import type { AssetsUsed, LegacyAssetsUsed, MatchedAssetsUsed, UsedAssetRef } from '@/api/types';

const NOT_EVALUATED: Record<string, string> = {
  not_evaluated: 'the asset catalog was being rebuilt',
  failed: 'asset matching failed',
  over_budget: 'the monthly AI budget was used up before asset matching could run',
};

interface Row extends UsedAssetRef {
  note: string;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Why GFT assets were not evaluated, or null when they were. */
export function notEvaluatedReason(used: AssetsUsed | null, evaluated: boolean): string | null {
  const key = used?.shape === 'matched' ? used.status : used?.shape;
  if (key && key in NOT_EVALUATED) return NOT_EVALUATED[key] ?? null;
  return evaluated ? null : (NOT_EVALUATED.not_evaluated ?? null);
}

/** What the lines under "Assets consulted" say about a run through asset matching. */
export function matchedLines(used: MatchedAssetsUsed): string[] {
  if (used.screened === 0 && used.status === 'no_match') return ['No asset was published when this run ran.'];
  const lines = [`${plural(used.screened, 'asset', 'assets')} screened.`];
  if (used.shortlisted.length === 0) lines.push('No strong match: no asset reached the shortlist.');
  if (used.partial) lines.push('Matching was partial: some assets could not be screened or analysed, so this list may be incomplete.');
  return lines;
}

function matchedRows(used: MatchedAssetsUsed): Row[] {
  const cited = new Set(used.cited.map((a) => a.id));
  return used.shortlisted.map((a) => ({ ...a, note: cited.has(a.id) ? 'named in the report' : 'considered' }));
}

/** What the sentence under "Assets consulted" says about how an old-catalog run met the catalog. */
export function legacyLine(used: LegacyAssetsUsed): string {
  if (used.mode === 'retrieval') return 'The catalog was too large to list, so the model searched it.';
  return used.carded.length === 0
    ? 'No asset was published when this run started.'
    : `The model was shown all ${used.carded.length} published assets and could search the catalog for detail.`;
}

/** Retrieved assets first, then any that were only cited (picked from the cards without a search). */
function legacyRows(used: LegacyAssetsUsed): Row[] {
  const cited = new Set(used.cited.map((a) => a.id));
  const rows = used.retrieved.map((a) => ({ ...a, note: cited.has(a.id) ? 'named in the report' : 'searched' }));
  const seen = new Set(rows.map((a) => a.id));
  return [...rows, ...used.cited.filter((a) => !seen.has(a.id)).map((a) => ({ ...a, note: 'named in the report' }))];
}

function Consulted({ lines, rows }: { lines: string[]; rows: Row[] }) {
  return (
    <div className="doc-sections-prose">
      <h3>Assets consulted</h3>
      {lines.map((line) => (
        <p key={line} className="hint">
          {line}
        </p>
      ))}
      {rows.length > 0 && (
        <ul>
          {rows.map((a) => (
            <li key={a.id}>
              {a.name}
              <span className="hint">{` (${a.note})`}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The GFT assets a run screened and shortlisted, under the GFT asset leverage section, so a reader can tell what the
 * report could draw on: the asset matching shape, the old catalog's shape, or why GFT assets were not evaluated.
 */
export function AssetsConsulted({ used, evaluated }: { used: AssetsUsed | null; evaluated: boolean }) {
  const reason = notEvaluatedReason(used, evaluated);
  if (reason) return <p className="hint">{`GFT assets were not evaluated for this research: ${reason}.`}</p>;
  if (used?.shape === 'matched') return <Consulted lines={matchedLines(used)} rows={matchedRows(used)} />;
  // A run made before the asset cards recorded nothing worth showing.
  if (used?.shape === 'legacy' && used.mode !== null) return <Consulted lines={[legacyLine(used)]} rows={legacyRows(used)} />;
  return null;
}
